import { resources } from "@constants/hardcoded-strings";
import { POLICY_COMPONENT_TYPE_OPTIONS } from "@constants/policy-component-type-options";
import { POLICY_CATEGORY_OPTIONS } from "@constants/policy-condition-category-options";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { CreatePolicyComponentGeneralStepValidationErrors } from "@idw-types/common/validation/create-policy-component/create-policy-component-validation-error";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { FormattedPolicyComponent } from "@idw-types/data/reference-data/generic/policy-component";
import { sortStrings } from "@utils/string-util";
import { ChangeEvent, useMemo, useState } from "react";

type PolicyComponentGeneralTabProps = {
    isDisabled: boolean;
    policyComponentState: FormattedPolicyComponent;
    axnCompanies: Array<AxnCompany>;
    validationErrors: CreatePolicyComponentGeneralStepValidationErrors;
    setPolicyComponentDetailsState: (policyComponentState: FormattedPolicyComponent) => void;
}

export const PolicyComponentGeneralTab = (props: PolicyComponentGeneralTabProps) => {
    const { isDisabled, policyComponentState, axnCompanies, validationErrors, setPolicyComponentDetailsState } = props;
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const createPolicyComponentResource = resources.modals.createPolicyComponent;
    const generalTabInputResources = resources.pages.policyComponentDetails.tabs.generalInformation.inputs;
    const [layout] = useState(LayoutTypes.ROW);


    const handlePolicyNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            name: event.target.value
        });
    }

    const handlePolicyComponentDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            description: event.target.value
        });
    }


    const handlePolicyComponentOrganizationChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            organizationID: selectedOption.value
        });
    }

    const handlePolicyComponentDecisionTypeChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            type: selectedOption.value
        });
    }

    const handlePolicyComponentCategoryChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            category: selectedOption.value
        });
    }


    const handlePolicyComponentStatusChange = () => {
        const newStatus = policyComponentState.status === EntityStatusTypes.ACTIVE
        ? EntityStatusTypes.INACTIVE
        : EntityStatusTypes.ACTIVE;
        
        setPolicyComponentDetailsState({
            ...policyComponentState,
            status: newStatus
        });
    }


    const organizationOptions = useMemo(() => {
        const options = axnCompanies.filter(company => company.type === "RP")
            .map(company => ({ label: company.name, value: company.id }))
            .sort((a, b) => sortStrings(a.label, b.label));
        options.unshift({ label: "IDDataweb (Public)", value: null });

        return options;
    }, [axnCompanies]);

    const selectedOrganization = useMemo(() => {
        return organizationOptions.find(org => org.value === policyComponentState.organizationID);
    }, [organizationOptions, policyComponentState.organizationID]);

    const selectedCategory = useMemo(() => {
        if (policyComponentState?.category) {
            return POLICY_CATEGORY_OPTIONS.find(category => category.value === policyComponentState.category) || null;
        }

        return null;

    }, [policyComponentState, POLICY_CATEGORY_OPTIONS]);

    const policyComponentTypeList = useMemo(() => {
        return [...POLICY_COMPONENT_TYPE_OPTIONS, { label: "LIST", value: "LIST" }];
    }, [POLICY_COMPONENT_TYPE_OPTIONS]);

    const selectedPolicyComponentTypeOption = useMemo(() => {
        return policyComponentTypeList.find(option => option.value === policyComponentState.type);
    }, [policyComponentTypeList, policyComponentState.type]);



    return (<div>
        <ContentPageHeader title={resources.pages.policyComponentDetails.tabs.generalInformation.title} />
        <ContentPageBody isMaxWidth={true}>
            <InputFormGroup
                controlId={"policy-component-details-name"}
                automationId={"policy-component-details-name"}
                label={createPolicyComponentResource.inputs.name.label}
                ariaLabel={createPolicyComponentResource.inputs.name.label}
                value={policyComponentState.name}
                disabled={isDisabled}
                isRequired={true}
                labelWidth={labelWidth}
                placeholder={""}
                onChange={handlePolicyNameChange}
            />
            {validationErrors.nameError}


            <TextareaFormGroup
                automationId={`policy-component-description-input`}
                controlId={`policy-component-description-input`}
                label={createPolicyComponentResource.inputs.description.label}
                ariaLabel={createPolicyComponentResource.inputs.description.label}
                value={policyComponentState.description}
                onChange={handlePolicyComponentDescriptionChange}
                disabled={isDisabled}
                isRequired={true}
                labelWidth={labelWidth}
                layout={layout}
            />
            {validationErrors.descriptionError}

            <SelectFormGroup
                automationId={`policy-component-organization-select`}
                controlId={`policy-component-organization-select`}
                label={createPolicyComponentResource.inputs.organization.label}
                ariaLabel={createPolicyComponentResource.inputs.organization.label}
                onChange={handlePolicyComponentOrganizationChange}
                options={organizationOptions}
                selectedOption={selectedOrganization}
                disabled={isDisabled || axnCompanies.length === 1}
                isRequired={axnCompanies.length === 1}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
                layout={layout}
            />

            <SelectFormGroup
                automationId={`policy-component-decision-type-select`}
                controlId={`policy-component-decision-type-select`}
                label={generalTabInputResources.decisionType.label}
                ariaLabel={generalTabInputResources.decisionType.label}
                onChange={handlePolicyComponentDecisionTypeChange}
                options={policyComponentTypeList}
                selectedOption={selectedPolicyComponentTypeOption}
                disabled={isDisabled}
                isRequired={true}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
                layout={layout}
            />

            <SelectFormGroup
                automationId={`policy-component-category-select`}
                controlId={`policy-component-category-select`}
                label={generalTabInputResources.class.label}
                ariaLabel={generalTabInputResources.class.label}
                onChange={handlePolicyComponentCategoryChange}
                options={POLICY_CATEGORY_OPTIONS}
                selectedOption={selectedCategory}
                disabled={isDisabled}
                isRequired={true}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
                layout={layout}
            />
             {validationErrors.categoryError}

            <ToggleFormGroup
                controlId={`policy-component-status-radio-input`}
                automationId={`policy-component-status-radio-input`}
                label={createPolicyComponentResource.inputs.status.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handlePolicyComponentStatusChange}
                checkedIcon={false}
                uncheckedIcon={false}
                checked={policyComponentState.status === EntityStatusTypes.ACTIVE}
                value={policyComponentState.status}
                isRequired={true}
                disabled={isDisabled}
            />

        </ContentPageBody>
    </div>);



}